import { useState } from 'react';
import Layout from '../components/layout/Layout';
import { Table, Button, Badge, Select, Input, MobileCardList } from '../components/common';

interface Product {
  id: string;
  name: string;
  category: string;
  batchNumber: string;
  quantity: number;
  weight: string;
  location: string;
  status: 'In Stock' | 'Low Stock' | 'Out of Stock';
  icon: string;
  iconColor: string;
  iconBg: string;
}

export default function ProductList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Mock data
  const products: Product[] = [
    {
      id: '1',
      name: 'Dell Laptop Pro',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-001',
      quantity: 25,
      weight: '50 kg',
      location: 'Zone A',
      status: 'In Stock',
      icon: 'fa-laptop',
      iconColor: '#1e3a5f',
      iconBg: 'rgba(30, 58, 95, 0.15)',
    },
    {
      id: '2',
      name: 'Office Chair Ergonomic',
      category: 'Furniture',
      batchNumber: 'BATCH-2026-002',
      quantity: 13,
      weight: '180 kg',
      location: 'Zone A',
      status: 'Low Stock',
      icon: 'fa-chair',
      iconColor: '#10b981',
      iconBg: 'rgba(16, 185, 129, 0.15)',
    },
    {
      id: '3',
      name: 'Wireless Mouse',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-003',
      quantity: 100,
      weight: '15 kg',
      location: 'Zone C',
      status: 'In Stock',
      icon: 'fa-mouse',
      iconColor: '#f59e0b',
      iconBg: 'rgba(245, 158, 11, 0.15)',
    },
    {
      id: '4',
      name: 'Monitor 24 inch',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-004',
      quantity: 45,
      weight: '120 kg',
      location: 'Zone C',
      status: 'In Stock',
      icon: 'fa-desktop',
      iconColor: '#1e3a5f',
      iconBg: 'rgba(30, 58, 95, 0.15)',
    },
    {
      id: '5',
      name: 'Mechanical Keyboard',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-005',
      quantity: 5,
      weight: '8 kg',
      location: 'Zone E',
      status: 'Low Stock',
      icon: 'fa-keyboard',
      iconColor: '#ef4444',
      iconBg: 'rgba(239, 68, 68, 0.15)',
    },
    {
      id: '6',
      name: 'USB Cable Pack',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-006',
      quantity: 200,
      weight: '10 kg',
      location: 'Zone B',
      status: 'In Stock',
      icon: 'fa-plug',
      iconColor: '#8b5cf6',
      iconBg: 'rgba(139, 92, 246, 0.15)',
    },
    {
      id: '7',
      name: 'Office Desk',
      category: 'Furniture',
      batchNumber: 'BATCH-2026-007',
      quantity: 8,
      weight: '250 kg',
      location: 'Zone A',
      status: 'Low Stock',
      icon: 'fa-table',
      iconColor: '#10b981',
      iconBg: 'rgba(16, 185, 129, 0.15)',
    },
    {
      id: '8',
      name: 'Printer Laser',
      category: 'Electronics',
      batchNumber: 'BATCH-2026-008',
      quantity: 15,
      weight: '80 kg',
      location: 'Zone D',
      status: 'In Stock',
      icon: 'fa-print',
      iconColor: '#1e3a5f',
      iconBg: 'rgba(30, 58, 95, 0.15)',
    },
  ];

  // Filter products
  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.batchNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || product.category === categoryFilter;
    const matchesStatus = !statusFilter || product.status === statusFilter;
    const matchesLocation = !locationFilter || product.location === locationFilter;
    
    return matchesSearch && matchesCategory && matchesStatus && matchesLocation;
  });

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  const columns = [
    {
      key: 'name',
      label: 'PRODUCT NAME',
      render: (value: string, row: Product) => (
        <div className="flex items-center">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center mr-3"
            style={{ background: row.iconBg }}
          >
            <i className={`fas ${row.icon}`} style={{ color: row.iconColor }}></i>
          </div>
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'CATEGORY',
    },
    {
      key: 'batchNumber',
      label: 'BATCH NUMBER',
    },
    {
      key: 'quantity',
      label: 'QUANTITY',
      render: (value: number) => <span>{value} units</span>,
    },
    {
      key: 'weight',
      label: 'WEIGHT',
    },
    {
      key: 'location',
      label: 'LOCATION',
      render: (value: string) => (
        <Badge variant="info" rounded>{value}</Badge>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      render: (value: string) => {
        const variant = value === 'In Stock' ? 'success' : value === 'Low Stock' ? 'warning' : 'danger';
        return <Badge variant={variant} rounded>{value}</Badge>;
      },
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      align: 'center' as const,
      render: () => (
        <button className="text-gray-600 hover:text-brand-600 transition-colors">
          <i className="fas fa-eye"></i>
        </button>
      ),
    },
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'Electronics', label: 'Electronics' },
    { value: 'Furniture', label: 'Furniture' },
    { value: 'Office Supplies', label: 'Office Supplies' },
  ];

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'In Stock', label: 'In Stock' },
    { value: 'Low Stock', label: 'Low Stock' },
    { value: 'Out of Stock', label: 'Out of Stock' },
  ];

  const locationOptions = [
    { value: '', label: 'All Locations' },
    { value: 'Zone A', label: 'Zone A' },
    { value: 'Zone B', label: 'Zone B' },
    { value: 'Zone C', label: 'Zone C' },
    { value: 'Zone D', label: 'Zone D' },
    { value: 'Zone E', label: 'Zone E' },
  ];

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Product List</h1>
          <p className="text-gray-600 mt-1">View all stored products</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-6 mb-6 border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <Input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon="fa-search"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <Select
                options={categoryOptions}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <Select
                options={locationOptions}
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              />
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Button variant="primary" icon="fa-search">
              Search
            </Button>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* Table Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center">
              <i className="fas fa-boxes text-brand-600 mr-2"></i>
              <h3 className="text-lg font-semibold text-gray-900">
                All Products ({filteredProducts.length})
              </h3>
            </div>
            <div className="flex space-x-2">
              <Button variant="secondary" icon="fa-download" size="sm">
                Export
              </Button>
              <Button variant="primary" icon="fa-plus" size="sm">
                Add Product
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="hidden lg:block">
            <Table
              columns={columns}
              data={currentProducts}
              hoverable
            />
          </div>
          <MobileCardList columns={columns} data={currentProducts} />

          {/* Pagination */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              Showing {startIndex + 1} to {Math.min(endIndex, filteredProducts.length)} of {filteredProducts.length} products
            </p>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-chevron-left text-xs"></i>
              </button>

              {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                const pageNum = idx + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-brand-600 text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}

              {totalPages > 5 && (
                <>
                  <span className="text-gray-400">...</span>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50`}
                  >
                    {totalPages}
                  </button>
                </>
              )}

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="fas fa-chevron-right text-xs"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
